import { BrowserSupport } from '@tableau/browser-support';
import Cookies from 'js-cookie';

import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Messages } from '../../codegen/Localize';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { getBooleanParamValue, getPathFromUrl, isMetricUrl, isSignInPath, isVizUrl } from '../../ts/helpers/UrlHelpers';

const HIDE_APP_BANNER_COOKIE = 'hideAppBanner';
// This parameter uses a colon prefix because it's the exact same parameter as used by vizclient.
const DEEP_LINKING_DISABLED_PARAM = ':deepLinkingDisabled';
const DISABLE_APP_BANNER_PARAM = 'disableAppBanner';
/**
 * Number of milliseconds to wait for the app to open before redirecting to the app store
 */
const REDIRECT_TO_APP_STORE_DELAY_IN_MS = 1000;
/**
 * Number of milliseconds to wait for the app to open before reloading the page with deep linking disabled
 */
const RELOAD_WITH_DEEP_LINKING_DISABLED_PARAM_DELAY_IN_MS = 1;

function getSessionManagementServiceService() {
    return getSessionManagementServiceInstance();
}

/**
 * TFSID 940092 - As of iOS 13, Safari on iPads will impersonate a desktop browser by default.
 * This makes it impossible to detect iPads by user agent alone. To get around this we infer
 * iPads in desktop browser mode by checking for a Macintosh user agent string and touch support.
 */
function isUserProbablyOnMobile() {
    return BrowserSupport.IsMobileBrowser() // Mobile browsers only
      || BrowserSupport.IsIOSDeviceInDesktopMode(); // and special case for ipad os
}

/**
 * Checks if we should show the custom app banner on mobile browsers.
 * We show the banner only on those pages to which we can deep link into the mobile app.
 * We don't show the banner if the banner has been dismissed by the user within the past 7 days.
 */
function shouldShowAppBanner(url: string) {
    // IE doesn't support the URL class. Running IE on a mobile device would be weird anyway.
    if (BrowserSupport.IsIE()) {
        return false;
    }

    const disableAppBannerFlag = getBooleanParamValue(url, DISABLE_APP_BANNER_PARAM);
    const isMetricsPageWithMetricsBanner = isMetricUrl(url) && getSessionManagementServiceService().isMetricsBannerEnabledForUser();

    return !disableAppBannerFlag // used by mobile app for web auth
      && FeatureFlags.customAppBanner // Feature flag
      && canDeepLinkToApp(url)
      && !Cookies.get(HIDE_APP_BANNER_COOKIE)
      && !isMetricsPageWithMetricsBanner;
  }

/**
 * Creates the deeplink. It is "tableau://[url to which we are supposed to get redirected]"
 * If there is no redirect url, then just use "tableau://[current url]"
 */
function createDeepLink(redirect: string, externalRedirect: string) {
    let deepLink = null;
    if (externalRedirect) {
      deepLink = 'tableau://' + WindowLocationService.getHost() + externalRedirect;
    } else {
      const protocol = WindowLocationService.getProtocol();
      deepLink = WindowLocationService.getHref();
      deepLink = deepLink.replace(protocol, 'tableau:');
      if (redirect) {
        deepLink = deepLink.split('#')[0] + '#' + redirect;
      }
    }
    return deepLink;
}

function onPressAppBanner() {
    const queryParams: any = WindowLocationService.getHashQueryObject();
    const deepLink = createDeepLink(queryParams[UrlParamKeys.Redirect], queryParams[UrlParamKeys.ExternalRedirect]);

    // This will trigger the browser to open the app if it's installed.
    (window as any).location = deepLink;

    // We redirect to the app store. This is delayed to give the browser a chance to
    // redirect to the app store.
    setTimeout(() => {
      if (BrowserSupport.IsIos()) {
        // This additional dialogue is necessary for iOS because iOS prompts a native dialogue
        // to open the app. Without the dialogue below, the browser will redirect to the app store
        // before the user is able to respond to the native dialogue. The dialogue below will show up
        // behind the native dialogue - this unusual behavior is expected.
        if (confirm(Messages.redirectToAppStore())) {
          (window as any).location = 'https://itunes.apple.com/app/tableau-mobile/id434633927';
        }
      } else if (BrowserSupport.IsAndroid()) {
        (window as any).location = 'https://play.google.com/store/apps/details?id=com.Tableau.TableauApp';
      }
    }, REDIRECT_TO_APP_STORE_DELAY_IN_MS);
  }

/**
 * We try to deep link only from those pages where we show the app banner.
 * In addition, we deep link even if the user has previously dismissed the app banner.
 * We try to redirect to the mobile app using tableau:// scheme when the page is first loaded.
 * We do not redirect on subsequent reloads of the page.
 * For details, see https://reviewboard.prod.tableautools.com/r/253096/diff/7/#7
 * @returns true if it is going to try to deep link into the app, false otherwise
 */
function tryDeepLinkOnLoad(locService: WindowLocationService): boolean {
    const url = locService.getHref();
    if (canDeepLinkToApp(url) // this is a page we can deep link to the mobile app
        && !BrowserSupport.IsFF() // is not Firefox, see TFSID 1021853
        && !containsParamsThatDisableDeepLinking(url) // url does not have params that disable deep linking
    ) {
      const queryParams: any = WindowLocationService.getHashQueryObject();
      const deepLink = createDeepLink(queryParams[UrlParamKeys.Redirect], queryParams[UrlParamKeys.ExternalRedirect]);

        // Reload the page, with deep linking disabled so that we don't go in circles.
        // This is delayed to give the browser a chance to redirect to the app.
        const deepLinkDisabled = addParamToDisableDeepLinking(url);
        setTimeout(() => redirectToUrl(locService, deepLinkDisabled), RELOAD_WITH_DEEP_LINKING_DISABLED_PARAM_DELAY_IN_MS);

        // This will trigger the browser to open the app if it's installed.
        redirectToUrl(locService, deepLink);
        return true;
    }
    return false;
}

function containsParamsThatDisableDeepLinking(url: string): boolean {
    return getBooleanParamValue(url, DEEP_LINKING_DISABLED_PARAM) ||
      getBooleanParamValue(url, UrlParamKeys.ClosePopupWhenDone) ||
      getBooleanParamValue(url, UrlParamKeys.Embedded);
}

function addParamToDisableDeepLinking(url: string): string {
  const newUrl = new URL(url);
  // Note: Do not use URLSearchParams to set query parameters.
  // See https://url.spec.whatwg.org/#example-constructing-urlsearchparams
  // URLSearchParams encodes the query parameters and messes up any space characters in the original url. E.g.,
  // 'https://qa-near.tsi.lan/views/TFS1234676Test/Shipping?%3Aembed=y&Region=East&Ship%20Mode=Second%20Class'
  // gets converted into
  // 'https://qa-near.tsi.lan/views/TFS1234676Test/Shipping?%3Aembed=y&Region=East&Ship+Mode=Second+Class&%3AdeepLinkingDisabled=y'
  // Notice the original url included space characters, encoded as '%20'. The usage of URLSearchParams results in
  // these '%20' characters getting encoded as '+', which is a legal conversion, except that it messes up our
  // server side code where '+' characters in the url get further encoded as '%2B'. So, by the time it finally
  // gets decoded, the space characters in the original url have been replaced by '+' characters, breaking URL filters,
  // and perhaps more.
  // See TFSID 1267026: [iPadOS Safari] URL filters with spaces not respected when applying Custom View
  // The code below returns the original url as is, the only difference being the addition of DEEP_LINKING_DISABLED_PARAM
  if (newUrl.search.length === 0) {
    newUrl.search = `?${DEEP_LINKING_DISABLED_PARAM}=y`;
  } else {
    newUrl.search += `&${DEEP_LINKING_DISABLED_PARAM}=y`;
  }
  return newUrl.href;
}

// helper functions

/**
 * Checks if we can deep link from this page to the mobile app.
 */
function canDeepLinkToApp(url: string) {
    return isUserProbablyOnMobile()
      && (isSignInPath(getPathFromUrl(url)) // sign-in and sign-in-site-selection pages
        || isVizUrl(url) // viz aka view page, e.g., 'http://hostname/#/views/Regional/Obesity'
        || isMetricUrl(url) // metric page, e.g., 'https://us-west-2a.online.tableau.com/#/site/alpodev/metrics/609'
      );
}

function redirectToUrl(locService: WindowLocationService, url: string) {
    locService.navigateToWithoutAddingToHistory(url);
}

export const MobileAppService = {
    addParamToDisableDeepLinking,
    containsParamsThatDisableDeepLinking,
    tryDeepLinkOnLoad,
    onPressAppBanner,
    isUserProbablyOnMobile,
    createDeepLink,
    shouldShowAppBanner,
    HIDE_APP_BANNER_COOKIE,
    RELOAD_WITH_DEEP_LINKING_DISABLED_PARAM_DELAY_IN_MS,
    DEEP_LINKING_DISABLED_PARAM
};

export type MobileAppService = typeof MobileAppService;
